import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";

import MembersHeader from "./components/MembersHeader";
import SearchBar from "./components/SearchBar";
import MembersTable from "./components/MembersTable";
import MembersMobile from "./components/MembersMobile";

export default function AdminMembers() {
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const MEMBERS_PER_PAGE = 25;

  useEffect(() => {
    loadMembers();
  }, []);

  async function loadMembers() {
    const token = localStorage.getItem("membreToken");

    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch("/api/admin/members", {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (res.status === 403) {
        localStorage.removeItem("membreToken");
        navigate("/login");
        return;
      }

      const json = await res.json();

      if (json.success) {
        const visibleMembers = (json.members || []).filter(
          (member) =>
            member.status === "approved" || member.status === "suspended",
        );

        setMembers(visibleMembers);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function getLevel(points) {
    if (points >= 600) return "Elite";
    if (points >= 300) return "Gold";
    if (points >= 100) return "Silver";

    return "Bronze";
  }

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const txt = search.toLowerCase();

      return (
        m.nom?.toLowerCase().includes(txt) ||
        m.prenom?.toLowerCase().includes(txt) ||
        m.email?.toLowerCase().includes(txt) ||
        m.member_id?.toLowerCase().includes(txt)
      );
    });
  }, [members, search]);
  const totalPages = Math.ceil(filteredMembers.length / MEMBERS_PER_PAGE);

  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * MEMBERS_PER_PAGE,
    currentPage * MEMBERS_PER_PAGE,
  );

  function exportExcel() {
    setExportLoading(true);

    const rows = filteredMembers.map((m, index) => ({
      "N°": index + 1,

      ID: m.member_id,

      Nom: m.nom,

      Prénom: m.prenom,

      Email: m.email,

      Téléphone: m.telephone,

      Points: m.points || 0,

      Niveau: getLevel(m.points || 0),

      "Date inscription": new Date(m.created_at).toLocaleDateString(),
    }));

    const sheet = XLSX.utils.json_to_sheet(rows);

    const book = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(book, sheet, "Membres");

    const buffer = XLSX.write(book, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(file, "membres.xlsx");

    setExportLoading(false);
  }
  function getPages() {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <MembersHeader
          count={filteredMembers.length}
          exportLoading={exportLoading}
          exportExcel={exportExcel}
        />

        <SearchBar
          value={search}
          onChange={(value) => {
            setSearch(value);
            setCurrentPage(1);
          }}
        />

        <MembersTable
          loading={loading}
          members={paginatedMembers}
          getLevel={getLevel}
          navigate={navigate}
        />

        <MembersMobile
          loading={loading}
          members={paginatedMembers}
          getLevel={getLevel}
          navigate={navigate}
        />
        <div className="flex flex-wrap justify-center items-center gap-2 mt-8">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-4 py-2 rounded-lg bg-gray-200 disabled:opacity-40"
          >
            ← Précédent
          </button>

          {getPages().map((page, index) => {
            if (page === "...") {
              return (
                <span key={index} className="px-2 text-gray-500">
                  ...
                </span>
              );
            }

            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-lg font-semibold transition ${
                  page === currentPage
                    ? "bg-blue-600 text-white"
                    : "bg-white hover:bg-blue-50"
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-4 py-2 rounded-lg bg-gray-200 disabled:opacity-40"
          >
            Suivant →
          </button>
        </div>
      </div>
    </div>
  );
}
